import React, { useState, useCallback } from 'react';
import { Box, Chip, Typography, IconButton, Tooltip } from '@mui/material';
import DeleteIcon from '@mui/icons-material/Delete';
import EditIcon from '@mui/icons-material/Edit';
import AddIcon from '@mui/icons-material/Add';
import HomeIcon from '@mui/icons-material/Home';
import ToggleOnIcon from '@mui/icons-material/ToggleOn';
import ToggleOffIcon from '@mui/icons-material/ToggleOff';
import { GridRenderCellParams, GridRowId } from '@mui/x-data-grid';

import PaginatedDataGrid, {
  PaginatedDataGridColumn,
  PaginatedDataGridAction
} from 'src/components/paginated-data-grid/PaginatedDataGrid';

import SimpleConfirmationDialog from 'src/components/SimpleConfirmationDialog';
import PropertyTypeDialog from './PropertyTypeDialog';
import { DataService } from 'src/services/data-service';
import { useAppDispatch } from 'src/redux/hooks';
import { showAlert } from 'src/redux/alert/alertReducer';

interface PropertyType {
  id: string;
  name: string;
  description: string;
  active: boolean;
  isCommercial: boolean;
}

const PropertyTypesListing: React.FC = () => {
  const [deleteId, setDeleteId] = useState<string | null>(null);
  const [refreshKey, setRefreshKey] = useState(0);
  const [editDialogOpen, setEditDialogOpen] = useState(false);
  const [selectedPropertyType, setSelectedPropertyType] = useState<PropertyType | null>(null);
  const [toggleLoading, setToggleLoading] = useState<string | null>(null);
  const dispatch = useAppDispatch();

  // ---------- Delete Property Type ----------
  const handleDeleteClick = (id: GridRowId) => {
    setDeleteId(String(id));
  };

  const handleDeleteConfirm = async () => {
    if (!deleteId) return;

    try {
      const response = await DataService.delete(`api/property-types/${deleteId}`, {});
      if (response.ok) {
        setRefreshKey((prev) => prev + 1);
        dispatch(
          showAlert({
            message: 'Property type deleted successfully!',
            type: 'success'
          })
        );
      } else {
        throw new Error('Failed to delete property type');
      }
    } catch (error) {
      dispatch(
        showAlert({
          message: 'Failed to delete property type. Please try again.',
          type: 'error'
        })
      );
    } finally {
      setDeleteId(null);
    }
  };

  const handleDeleteCancel = () => {
    setDeleteId(null);
  };

  // ---------- Edit Property Type ----------
  const handleEditClick = (propertyType: PropertyType) => {
    setSelectedPropertyType(propertyType);
    setEditDialogOpen(true);
  };

  // ---------- Add Property Type ----------
  const handleAddPropertyType = () => {
    setSelectedPropertyType(null);
    setEditDialogOpen(true);
  };

  // ---------- Toggle Active/Inactive ----------
  const handleToggleActive = async (propertyType: PropertyType) => {
    setToggleLoading(propertyType.id);

    try {
      const endpoint = propertyType.active
        ? `api/property-types/deactivate?id=${propertyType.id}`
        : `api/property-types/activate?id=${propertyType.id}`;

      const response = await DataService.patch(endpoint, {});

      if (response.ok) {
        setRefreshKey((prev) => prev + 1);
        dispatch(
          showAlert({
            message: `Property type ${propertyType.active ? 'deactivated' : 'activated'} successfully!`,
            type: 'success'
          })
        );
      } else {
        throw new Error('Failed to update property type status');
      }
    } catch (error) {
      dispatch(
        showAlert({
          message: 'Failed to update property type status. Please try again.',
          type: 'error'
        })
      );
    } finally {
      setToggleLoading(null);
    }
  };

  // ---------- Handle Dialog Success ----------
  const handleDialogSuccess = () => {
    setEditDialogOpen(false);
    setRefreshKey((prev) => prev + 1);
    dispatch(
      showAlert({
        message: selectedPropertyType ? 'Property type updated successfully!' : 'Property type created successfully!',
        type: 'success'
      })
    );
  };

  // ---------- Data Grid Columns ----------
  const columns: PaginatedDataGridColumn[] = [
    {
      field: 'name',
      headerName: 'Property Type',
      flex: 1.5,
      minWidth: 180,
      renderCell: (params: GridRenderCellParams) => (
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <HomeIcon color="primary" fontSize="small" />
          <Typography variant="body2" sx={{ fontWeight: 'medium' }}>
            {params.value}
          </Typography>
        </Box>
      )
    },
    {
      field: 'description',
      headerName: 'Description',
      flex: 2,
      minWidth: 200,
      renderCell: (params: GridRenderCellParams) => (
        <Typography variant="body2" sx={{ color: 'text.secondary' }}>
          {params.value || (
            <span style={{ fontStyle: 'italic' }}>
              No description provided
            </span>
          )}
        </Typography>
      )
    },
    {
      field: 'isCommercial',
      headerName: 'Type',
      width: 140,
      renderCell: (params: GridRenderCellParams) => (
        <Chip
          label={params.value ? 'Commercial' : 'Residential'}
          color={params.value ? 'info' : 'primary' }
          size="small"
          variant="outlined"
        />
      )
    },
    {
      field: 'active',
      headerName: 'Status',
      width: 120,
      renderCell: (params: GridRenderCellParams) => (
        <Chip
          label={params.value ? 'Active' : 'Inactive'}
          color={params.value ? 'success' : 'default'}
          size="small"
          variant="outlined"
        />
      )
    },
    {
      field: 'actions',
      headerName: 'Actions',
      width: 120,
      sortable: false,
      renderCell: (params: GridRenderCellParams) => (
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
          <Tooltip title={params.row.active ? 'Deactivate' : 'Activate'}>
            <IconButton
              size="small"
              onClick={() => handleToggleActive(params.row)}
              disabled={toggleLoading === params.row.id}
              color={params.row.active ? 'success' : 'default'}
            >
              {params.row.active ? <ToggleOnIcon /> : <ToggleOffIcon />}
            </IconButton>
          </Tooltip>
          <Tooltip title="Edit">
            <IconButton
              size="small"
              onClick={() => handleEditClick(params.row)}
              color="primary"
            >
              <EditIcon fontSize="small" />
            </IconButton>
          </Tooltip>
          <Tooltip title="Delete">
            <IconButton
              size="small"
              onClick={() => handleDeleteClick(params.row.id)}
              color="error"
            >
              <DeleteIcon fontSize="small" />
            </IconButton>
          </Tooltip>
        </Box>
      )
    }
  ];

  // ---------- Data Grid Actions (Toolbar) ----------
  const actions: PaginatedDataGridAction[] = [
    {
      key: 'add',
      title: 'Add Property Type',
      icon: <AddIcon />,
      onClick: handleAddPropertyType,
      disabled: () => false,
      color: 'primary'
    }
  ];

  // ---------- Fetch Property Types Function ----------
  const fetchPropertyTypes = useCallback(
    async (params: {
      page: number;
      pageSize: number;
      sortModel?: any;
      filterModel?: any;
      search?: string;
    }) => {
      console.log('fetchPropertyTypes called with params:', params);
      try {
        const response = await DataService.get('api/property-types/all');

        if (response.ok) {
          const responseData = await response.json();
          console.log('API Response:', responseData);

          let rawPropertyTypes: any[] = [];

          if (responseData.success && responseData.data && Array.isArray(responseData.data)) {
            rawPropertyTypes = responseData.data;
          } else if (Array.isArray(responseData)) {
            rawPropertyTypes = responseData;
          } else {
            console.error('Unexpected response structure:', responseData);
            throw new Error('Invalid response structure');
          }

          const propertyTypes: PropertyType[] = rawPropertyTypes.map(propertyType => ({
            id: propertyType.id,
            name: propertyType.name,
            description: propertyType.description,
            active: propertyType.active,
            isCommercial: propertyType.isCommercial
          }));

          console.log('Raw property types from API:', rawPropertyTypes);
          console.log('Mapped property types:', propertyTypes);

          // Filter by search term if provided
          let filteredPropertyTypes = propertyTypes;
          if (params.search) {
            const searchLower = params.search.toLowerCase();
            filteredPropertyTypes = propertyTypes.filter(
              propertyType =>
                propertyType.name.toLowerCase().includes(searchLower) ||
                (propertyType.description && propertyType.description.toLowerCase().includes(searchLower))
            );
          }

          // Apply sorting if provided
          if (params.sortModel && params.sortModel.length > 0) {
            const sortModel = params.sortModel[0];
            filteredPropertyTypes.sort((a, b) => {
              let aValue: any = a[sortModel.field as keyof PropertyType];
              let bValue: any = b[sortModel.field as keyof PropertyType];

              // Handle string comparison
              if (typeof aValue === 'string') {
                aValue = aValue.toLowerCase();
                bValue = (bValue as string).toLowerCase();
              }

              if (sortModel.sort === 'desc') {
                return aValue < bValue ? 1 : -1;
              }
              return aValue > bValue ? 1 : -1;
            });
          }

          // Apply pagination (DataGrid uses 0-based page indexing)
          const startIndex = params.page * params.pageSize;
          const paginatedPropertyTypes = filteredPropertyTypes.slice(startIndex, startIndex + params.pageSize);

          const totalPages = Math.ceil(filteredPropertyTypes.length / params.pageSize);

          const result = {
            items: paginatedPropertyTypes,
            totalCount: filteredPropertyTypes.length,
            pageNumber: params.page + 1,
            pageSize: params.pageSize,
            totalPages: totalPages,
            hasNext: params.page + 1 < totalPages,
            hasPrevious: params.page > 0
          };

          console.log('Final result being returned:', result);
          return result;
        } else {
          throw new Error(`Failed to fetch property types: ${response.status} ${response.statusText}`);
        }
      } catch (error) {
        console.error('Error fetching property types:', error);
        const errorMessage = error instanceof Error ? error.message : 'Failed to load property types. Please try again.';
        dispatch(
          showAlert({
            message: errorMessage,
            type: 'error'
          })
        );
        return {
          items: [],
          totalCount: 0,
          pageNumber: params.page + 1,
          pageSize: params.pageSize,
          totalPages: 0,
          hasNext: false,
          hasPrevious: false
        };
      }
    },
    [dispatch]
  );

  return (
    <Box sx={{ width: '100%', height: '100%' }}>
      <Typography variant="h5" sx={{ mb: 1.5, fontWeight: 'bold' }}>
        Property Types Management
      </Typography>

      <PaginatedDataGrid
        key={refreshKey}
        title="Property Types"
        columns={columns}
        useBackendPagination
        onDataFetch={fetchPropertyTypes}
        actions={actions}
        emptyMessage="No property types found"
      />

      {/* Delete Confirmation Dialog */}
      <SimpleConfirmationDialog
        open={!!deleteId}
        title="Delete Property Type"
        message="Are you sure you want to delete this property type? This action cannot be undone."
        onConfirm={handleDeleteConfirm}
        onClose={handleDeleteCancel}
      />

      {/* Add/Edit Property Type Dialog */}
      <PropertyTypeDialog
        open={editDialogOpen}
        propertyType={selectedPropertyType}
        onClose={() => setEditDialogOpen(false)}
        onSuccess={handleDialogSuccess}
      />
    </Box>
  );
};

export default PropertyTypesListing;